"use client";

import { c as _c } from "react/compiler-runtime";
import { Refresh04Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { type PtrPhase, usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { haptic } from "@/lib/haptics";
import { cn } from "@/lib/utils";
const INDICATOR_SIZE = 40;
const HIDDEN_OFFSET = -(INDICATOR_SIZE + 16);
const EASE_OUT_QUINT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_SETTLE = "cubic-bezier(0.32, 0.72, 0, 1)";
const RETRACT_MS = 320;
const SETTLE_MS = 180;
const BOUNCE_MS = 220;
export interface PullToRefreshOverride {
  onRefresh?: () => Promise<void> | void;
  disabled?: boolean;
}
const overrideStack = new Map<string, PullToRefreshOverride>();
function activeOverride(): PullToRefreshOverride | undefined {
  let last: PullToRefreshOverride | undefined;
  for (const entry of overrideStack.values()) last = entry;
  return last;
}
export function usePullToRefreshOverride(override: PullToRefreshOverride): void {
  const id = useId();
  const overrideRef = useRef(override);
  overrideRef.current = override;
  useEffect(() => {
    overrideStack.set(id, overrideRef.current);
    return () => {
      overrideStack.delete(id);
    };
  }, [id]);
  useEffect(() => {
    overrideStack.set(id, override);
  }, [id, override]);
}
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
function useAppHeaderOffset() {
  const $ = _c(2);
  const [offset, setOffset] = useState(0);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const compute = () => {
        const headers = document.querySelectorAll("[data-app-header]");
        let max = 0;
        headers.forEach(el => {
          if (el.offsetParent === null) {
            return;
          }
          const rect = el.getBoundingClientRect();
          if (rect.bottom > max) {
            max = rect.bottom;
          }
        });
        setOffset(Math.max(0, Math.round(max)));
      };
      compute();
      const ro = new ResizeObserver(compute);
      document.querySelectorAll("[data-app-header]").forEach(el_0 => {
        ro.observe(el_0);
      });
      window.addEventListener("resize", compute);
      window.addEventListener("orientationchange", compute);
      const mo = new MutationObserver(() => {
        ro.disconnect();
        document.querySelectorAll("[data-app-header]").forEach(el_1 => {
          ro.observe(el_1);
        });
        compute();
      });
      mo.observe(document.body, {
        childList: true,
        subtree: true
      });
      return () => {
        ro.disconnect();
        mo.disconnect();
        window.removeEventListener("resize", compute);
        window.removeEventListener("orientationchange", compute);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return offset;
}
export function PullToRefresh() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const headerOffset = useAppHeaderOffset();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const iconWrapRef = useRef<HTMLDivElement | null>(null);
  const phaseRef = useRef<PtrPhase>("idle");
  const statusRef = useRef<HTMLSpanElement | null>(null);
  const handleRefresh = useCallback(async () => {
    haptic("impact");
    const override = activeOverride();
    try {
      if (override?.onRefresh) {
        await override.onRefresh();
      } else {
        try {
          router.refresh();
        } catch {
          /* noop */
        }
        await queryClient.invalidateQueries({
          refetchType: "active"
        });
      }
      haptic("success");
    } catch {
      haptic("warning");
    }
  }, [queryClient, router]);
  const applyTransform = useCallback((offset: number, progress: number) => {
    const wrap = wrapRef.current;
    const bubble = bubbleRef.current;
    const iconWrap = iconWrapRef.current;
    if (!wrap || !bubble || !iconWrap) return;
    const translate = offset <= 0 ? HIDDEN_OFFSET : offset - INDICATOR_SIZE - 12;
    const reduced = prefersReducedMotion();
    const scale = reduced ? 1 : 0.85 + 0.15 * Math.min(1, progress);
    const opacity = offset <= 0 ? 0 : Math.min(1, progress * 2);
    wrap.style.transform = `translate3d(0, ${translate}px, 0)`;
    wrap.style.opacity = String(opacity);
    bubble.style.transform = `scale(${scale})`;
    if (phaseRef.current !== "refreshing") {
      iconWrap.style.transform = `rotate(${progress * 180}deg)`;
      iconWrap.style.opacity = String(0.5 + 0.5 * Math.min(1, progress));
    }
  }, []);
  const playBounce = useCallback(() => {
    if (prefersReducedMotion()) return;
    const bubble_0 = bubbleRef.current;
    if (!bubble_0) return;
    bubble_0.animate([{
      transform: "scale(1)"
    }, {
      transform: "scale(1.08)",
      offset: 0.45
    }, {
      transform: "scale(1)"
    }], {
      duration: BOUNCE_MS,
      easing: EASE_SETTLE,
      fill: "none"
    });
  }, []);
  const {
    phase
  } = usePullToRefresh({
    onRefresh: handleRefresh,
    onOffsetChange: applyTransform,
    onThresholdCross: () => {
      haptic("selection");
      playBounce();
      if (statusRef.current) statusRef.current.textContent = "Release to refresh";
    },
    getDisabled: () => activeOverride()?.disabled === true,
    onPhaseChange: next => {
      const prev = phaseRef.current;
      phaseRef.current = next;
      const wrap_0 = wrapRef.current;
      const bubble_1 = bubbleRef.current;
      const iconWrap_0 = iconWrapRef.current;
      if (!wrap_0 || !bubble_1 || !iconWrap_0) return;
      if (next === "idle") {
        wrap_0.style.transition = `transform ${RETRACT_MS}ms ${EASE_OUT_QUINT}, opacity ${RETRACT_MS}ms ${EASE_OUT_QUINT}`;
        wrap_0.style.transform = `translate3d(0, ${HIDDEN_OFFSET}px, 0)`;
        wrap_0.style.opacity = "0";
        bubble_1.style.transition = `transform ${RETRACT_MS}ms ${EASE_OUT_QUINT}`;
        bubble_1.style.transform = "scale(0.85)";
        window.setTimeout(() => {
          if (!wrapRef.current || !bubbleRef.current) return;
          wrapRef.current.style.transition = "";
          bubbleRef.current.style.transition = "";
          if (iconWrapRef.current) {
            iconWrapRef.current.style.opacity = "0.5";
            iconWrapRef.current.style.transform = "rotate(0deg)";
          }
        }, RETRACT_MS + 20);
        iconWrap_0.style.animation = "";
        if (statusRef.current && prev === "refreshing") {
          statusRef.current.textContent = "Up to date";
        }
      } else if (next === "pulling") {
        wrap_0.style.transition = "";
        bubble_1.style.transition = "";
        iconWrap_0.style.animation = "";
        if (statusRef.current) statusRef.current.textContent = "Pull to refresh";
      } else if (next === "refreshing") {
        wrap_0.style.transition = `transform ${SETTLE_MS}ms ${EASE_SETTLE}, opacity ${SETTLE_MS}ms ${EASE_SETTLE}`;
        bubble_1.style.transition = `transform ${SETTLE_MS}ms ${EASE_SETTLE}`;
        bubble_1.style.transform = "scale(1)";
        iconWrap_0.style.opacity = "1";
        if (!prefersReducedMotion()) {
          iconWrap_0.style.animation = "ptr-rotate 1s linear infinite";
        }
        if (statusRef.current) statusRef.current.textContent = "Refreshing";
      }
    }
  });
  return <>
			<style>
				{`
@keyframes ptr-rotate {
  to { transform: rotate(360deg); }
}
@keyframes ptr-dots {
  0% { content: ""; }
  25% { content: "."; }
  50% { content: ".."; }
  75%, 100% { content: "..."; }
}
.animate-ptr-dots::after {
  content: "";
  animation: ptr-dots 1.5s infinite steps(1);
}
`}
			</style>
			<div ref={wrapRef} aria-hidden={phase === "idle"} className={cn("pointer-events-none fixed inset-x-0 z-[51] flex justify-center")} style={{
      top: `calc(env(safe-area-inset-top) + ${headerOffset}px + 8px)`,
      transform: `translate3d(0, ${HIDDEN_OFFSET}px, 0)`,
      opacity: 0,
      willChange: "transform, opacity"
    }}>
				<output className={cn("flex h-10 items-center justify-start overflow-hidden rounded-full border border-border bg-background text-foreground shadow-sm", "transition-all duration-300 ease-out", phase === "refreshing" ? "w-[144px] pr-3" : "w-10 pr-0")} style={{
        transformOrigin: "center"
      }}>
					<div ref={bubbleRef} className="relative flex size-10 shrink-0 items-center justify-center rounded-full" style={{
          transformOrigin: "center",
          transform: "scale(0.85)",
          willChange: "transform"
        }}>
						<div ref={iconWrapRef} className="flex items-center justify-center" style={{
            opacity: 0.5,
            willChange: "opacity, transform"
          }}>
							<HugeiconsIcon icon={Refresh04Icon} className="size-5" strokeWidth={1.75} />
						</div>
					</div>
					<div className={cn("flex items-center overflow-hidden whitespace-nowrap font-medium text-foreground/80 text-sm tracking-tight transition-all duration-300 ease-out", phase === "refreshing" ? "w-[92px] opacity-100" : "w-0 opacity-0")}>
						<span className="w-full animate-pulse">
							Warming up
							<span className="animate-ptr-dots" />
						</span>
					</div>
					<span ref={statusRef} className="sr-only" aria-live="polite" />
				</output>
			</div>
		</>;
}